/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import Subject from '../../rx/Subject';
import PublisherState from './PublisherState';
import EndPoint, {
  IStream,
  AddIceCandidatesStatus,
  SetRemoteDescriptionStatus
} from '../discovery/EndPoint';
import assertUnreachable from '../../lang/assertUnreachable';
import Strings from '../../lang/Strings';
import DisposableList from '../../lang/DisposableList';
import {IRtcPublishMonitorStatistic} from '../../rtc/RtcPublishMonitor';
import {PublishStatus} from '../api/Publish';

export default class PublisherContext {
  publisherInitialization: Date;
  disposables: DisposableList;
  publisherDisposables: DisposableList;
  state: Subject<PublisherState>;
  authorized: Subject<boolean>;
  online: Subject<boolean>;
  loading: Subject<boolean>;
  publishing: Subject<boolean>;
  standby: Subject<boolean>;
  stopped: Subject<boolean>;
  failureCount: Subject<number>;
  endPoint: Subject<EndPoint | null>;
  stream: Subject<IStream | null>;
  rtcStatistics: Subject<IRtcPublishMonitorStatistic | null>;
  clearFailureCountTimeout: number;
  startReconnectionAttemptsTimeInMilliseconds: number;
  name: string;

  constructor(name?: string) {
    this.disposables = new DisposableList();
    this.publisherDisposables = new DisposableList();
    this.publisherInitialization = new Date();
    this.state = new Subject<PublisherState>(PublisherState.Initializing);
    this.authorized = new Subject<boolean>(true);
    this.online = new Subject<boolean>(true);
    this.loading = new Subject<boolean>(false);
    this.publishing = new Subject<boolean>(false);
    this.standby = new Subject<boolean>(false);
    this.stopped = new Subject<boolean>(false);
    this.failureCount = new Subject<number>(0);
    this.endPoint = new Subject<EndPoint | null>(null);
    this.stream = new Subject<IStream | null>(null);
    this.rtcStatistics = new Subject<IRtcPublishMonitorStatistic | null>(null);
    this.clearFailureCountTimeout = 0;
    this.name = name && name.length ? name : this.generateScreenName();
    this.startReconnectionAttemptsTimeInMilliseconds = 0;
  }

  get streamId(): string {
    const stream = this.stream.value;

    if (!stream) {
      return '-';
    }

    return stream.streamId;
  }

  applyPublishStatus(status: PublishStatus): void {
    switch (status) {
      case 'ok':
        return;
      case 'unauthorized':
      case 'geo-restricted':
      case 'geo-blocked':
        this.applyUnauthorized();

        return;
      case 'not-found':
        this.applyNotFound();

        return;
      default:
        this.applyUnhandledError();

        return;
    }
  }

  applySetRemoteDescriptionStatus(status: SetRemoteDescriptionStatus): void {
    switch (status) {
      case 'ok':
        return;
      case 'unauthorized':
        this.applyUnauthorized();

        return;
      case 'not-found':
        this.applyNotFound();

        return;
      default:
        this.applyUnhandledError();

        return;
    }
  }

  applyAddIceCandidatesStatus(status: AddIceCandidatesStatus): void {
    switch (status) {
      case 'ok':
        return;
      case 'unauthorized':
        this.applyUnauthorized();

        return;
      case 'not-found':
        this.applyNotFound();

        return;
      default:
        this.applyUnhandledError();

        return;
    }
  }

  mapPublishStatusToPublisherStatus(status: PublishStatus): PublisherState {
    switch (status) {
      case 'ok':
        return PublisherState.Starting;
      case 'not-found':
        return PublisherState.NotFound;
      case 'geo-restricted':
        return PublisherState.GeoRestricted;
      case 'geo-blocked':
        return PublisherState.GeoBlocked;
      case 'unauthorized':
        return PublisherState.Unauthorized;
      case 'capacity':
      case 'rate-limited':
      case 'timeout':
        return PublisherState.Recovering;
      case 'missing-channel-or-room-id':
      case 'forbidden-tag':
        return PublisherState.ConfigurationError;
      case 'port-in-range-not-found':
        return PublisherState.TransientConfigurationError;
      case 'failed':
        return PublisherState.Error;
      default:
        assertUnreachable(status);
    }
  }

  applyUnhandledError(): void {
    this.failureCount.value++;
    this.publishing.value = false;
    this.standby.value = true;
    this.stopped.value = false;
    this.stream.value = null;
  }

  applyUnauthorized(): void {
    this.authorized.value = false;
    this.failureCount.value = 0;
    this.publishing.value = false;
    this.standby.value = true;
    this.stopped.value = false;
    this.stream.value = null;
  }

  applyNotFound(): void {
    this.failureCount.value = 0;
    this.publishing.value = false;
    this.standby.value = true;
    this.stopped.value = false;
    this.stream.value = null;
  }

  private generateScreenName(): string {
    return Strings.random(12);
  }
}